/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { breakpoints } from '@coursera/cds-core';

import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import { usePageContext } from 'bundles/page-config-common/contexts/PageContext';

type PropsForHomeSection = {
  children: React.ReactNode;
  spacing?: number;
  className?: string;
  dataTestId?: string;
  onlyTopPadding?: boolean;
};

const styles = {
  separator: css`
    &::before,
    &::after {
      content: '';
      display: table;
    }
  `,
  marginEnterpriseRefreshExperiment: (spacing: number) => css`
    padding: ${spacing + 16}px 0;

    ${breakpoints.down('sm')} {
      padding: ${spacing + 12}px 0;
    }
  `,
  marginEnterprise: (spacing: number) => css`
    margin: ${spacing + 30}px auto;
    padding: 30px 0;

    ${breakpoints.down('md')} {
      margin: ${spacing}px auto;
    }
  `,
  marginConsumer: (spacing: number) => css`
    margin: ${spacing + 30}px auto;

    ${breakpoints.down('md')} {
      margin: ${spacing + 15}px auto;
    }
  `,
  onlyTopPadding: (spacing: number) => css`
    padding-bottom: 0;
    padding-top: ${spacing + 32}px;

    ${breakpoints.down('sm')} {
      padding-top: ${spacing + 24}px;
    }
  `,
};

/**
 * @description Custom margins for page sections on the home page
 */
const HomeSection = ({
  children,
  spacing,
  className,
  dataTestId,
  onlyTopPadding,
}: PropsForHomeSection): JSX.Element | null => {
  const pageContext = usePageContext();
  const enableEnterpriseHomeRefresh = GrowthDiscoveryEpicClient.preview('enableEnterpriseHomeRefresh');

  if (pageContext?.isConsumer) {
    return (
      <div className={className} css={styles.separator} data-testid={dataTestId}>
        <div css={styles.marginConsumer(spacing || 0)} data-testid="margin-div" className="HomeSection">
          {children}
        </div>
      </div>
    );
  } else {
    return (
      <div className={className} css={styles.separator} data-testid={dataTestId}>
        <div
          css={[
            enableEnterpriseHomeRefresh
              ? styles.marginEnterpriseRefreshExperiment(spacing || 0)
              : styles.marginEnterprise(spacing || 0),
            onlyTopPadding && styles.onlyTopPadding(spacing || 0),
          ]}
          data-testid="margin-div"
          className="HomeSection"
        >
          {children}
        </div>
      </div>
    );
  }
};

export default HomeSection;
